import type { Recipe } from '../types/inventory';

// Fase 22C-2: lógica pura de la asignación de receta a un producto de Ventas Otros (solo futuras
// ventas). Extraída del componente para poder probarla sin renderizador (el proyecto no usa
// testing-library ni añade dependencias).

export type RecipeChangeKind = 'none' | 'assign' | 'change' | 'remove';

export function recipeChangeKind(original: string, next: string): RecipeChangeKind {
  if (original === next) return 'none';
  if (!original && next) return 'assign';
  if (original && !next) return 'remove';
  return 'change';
}

// Mensaje de confirmación. Siempre aclara que las ventas anteriores NO cambian y que la nueva
// configuración solo aplica a futuras ventas.
export function recipeChangeConfirmation(kind: RecipeChangeKind): string | null {
  switch (kind) {
    case 'assign':
      return 'Esta receta se aplicará únicamente a ventas futuras. Las ventas anteriores no cambian.';
    case 'change':
      return 'Las ventas futuras usarán la nueva receta. Las ventas anteriores conservan la receta con la que se registraron.';
    case 'remove':
      return 'Al retirar la receta, el producto dejará de afectar inventario en ventas futuras. Las ventas anteriores conservan sus consumos y movimientos.';
    default:
      return null;
  }
}

// P-071: lo que hay que confirmar al cambiar CÓMO se lleva el costo (NONE ↔ APPROXIMATE ↔ RECIPE) o su
// valor. Reutiliza los textos de receta cuando el cambio es de receta; para el costo aproximado dice lo
// mismo con sus palabras. Siempre: las ventas anteriores no cambian; solo ventas futuras. null = nada que
// confirmar (no cambió el modo, ni la receta, ni el costo).
export interface CostAssignment {
  costMode: 'NONE' | 'APPROXIMATE' | 'RECIPE';
  recipeId: string;
  /** Texto del formulario ('' = sin valor); se compara como número con dos decimales. */
  approximateUnitCost: string;
}

export function costChangeConfirmation(original: CostAssignment, next: CostAssignment): string | null {
  if (original.costMode === 'RECIPE' && next.costMode === 'RECIPE') return recipeChangeConfirmation(recipeChangeKind(original.recipeId, next.recipeId));
  if (original.costMode === 'NONE' && next.costMode === 'RECIPE') return recipeChangeConfirmation('assign');
  if (original.costMode === 'RECIPE' && next.costMode === 'NONE') return recipeChangeConfirmation('remove');
  if (original.costMode === next.costMode && next.costMode === 'NONE') return null;
  if (next.costMode === 'APPROXIMATE') {
    if (original.costMode === 'APPROXIMATE') {
      return sameAmount(original.approximateUnitCost, next.approximateUnitCost)
        ? null
        : 'El nuevo costo aproximado se aplicará únicamente a ventas futuras. Las ventas anteriores conservan el costo con el que se registraron.';
    }
    return original.costMode === 'RECIPE'
      ? 'A partir de ahora el costo será el aproximado que escribas y el producto dejará de descontar inventario en ventas futuras. Las ventas anteriores conservan su receta, sus consumos y su costo.'
      : 'El costo aproximado se aplicará únicamente a ventas futuras. Las ventas anteriores no cambian.';
  }
  // Desde APPROXIMATE hacia RECIPE o NONE.
  return next.costMode === 'RECIPE'
    ? 'Las ventas futuras usarán la receta y descontarán inventario. Las ventas anteriores conservan su costo aproximado.'
    : 'Al quitar el costo aproximado, las ventas futuras quedarán sin costo definido. Las ventas anteriores conservan el suyo.';
}

/** El mismo importe exacto («12.50» y «12,5» lo son; «1.00» y «1.005» no: lo escrito no se redondea para comparar). */
function sameAmount(left: string, right: string): boolean {
  const a = Number(left.trim().replace(',', '.'));
  const b = Number(right.trim().replace(',', '.'));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return left.trim() === right.trim();
  return a === b;
}

export interface RecipeOption {
  id: string;
  label: string;
  /** No seleccionable como asignación nueva (receta inactiva no asignada actualmente). */
  disabled: boolean;
}

/**
 * Opciones elegibles para asignar: recetas ACTIVAS. Una receta inactiva se lista SOLO si es la
 * asignada actualmente (para mostrar "Asignada, pero inactiva"), marcada como no seleccionable.
 */
export function eligibleRecipeOptions(recipes: Recipe[], currentRecipeId: string | null): RecipeOption[] {
  const options: RecipeOption[] = [];
  for (const recipe of recipes) {
    if (recipe.isActive) {
      options.push({ id: recipe.id, label: recipe.name, disabled: false });
    } else if (recipe.id === currentRecipeId) {
      options.push({ id: recipe.id, label: `${recipe.name} (inactiva)`, disabled: true });
    }
  }
  return options;
}
